import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  photoUrl: string | null;
  stationName: string;
};

export function StationPhoto({ photoUrl, stationName }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (!photoUrl) {
    return (
      <div
        data-testid="photo-placeholder"
        className="surface-mesh flex h-24 w-24 shrink-0 items-center justify-center rounded-lg text-xs text-fg-mute"
      >
        {t("stations.photo_pending")}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("stations.photo_open")}
        className="h-24 w-24 shrink-0 overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-volt"
      >
        <img src={photoUrl} alt={stationName} className="h-full w-full object-cover" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("stations.photo_aria")}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        >
          <img
            src={photoUrl}
            alt={stationName}
            className="max-h-[90dvh] max-w-[90dvw] rounded-lg object-contain shadow-2xl"
          />
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={() => setOpen(false)}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
    </>
  );
}
